import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, DestroyRef, booleanAttribute, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatIcon } from '@angular/material/icon';

import { SpinnerComponent } from './spinner.component';

/** How long a field waits before searching on what has been typed into it. */
export const SEARCH_DEBOUNCE_MS = 250;

/** Below the field, and above it where the field is too near the bottom of the screen to fit a list. */
export const OVERLAY_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
];

/**
 * Measured when the list opens, never in a binding: a binding evaluates while the element is still
 * being created and measures zero, which the overlay reads as no width at all and then spreads from
 * the field to the edge of the screen.
 */
export function fieldWidth(origin: CdkOverlayOrigin): number {
  return origin.elementRef.nativeElement.getBoundingClientRect().width;
}

/** A click on a result has to land before the blur that closes the list carrying it. */
const CLOSE_DELAY_MS = 150;

/**
 * The one type-to-search field in this UI: leading icon, the box itself, and a spinner while the
 * answer is in flight. Every lookup is built on it, so each is asked for in a control of the same
 * height that closes on blur after the same delay.
 *
 * The host is the overlay origin, so a caller anchoring a list to the field binds
 * `cdkOverlayOrigin` here rather than wrapping it in another element.
 */
@Component({
  selector: 'guarantees-search-field',
  standalone: true,
  imports: [FormsModule, MatIcon, SpinnerComponent],
  host: { class: 'relative block' },
  template: `
    <span class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-neutral-400">
      <mat-icon class="h-4! w-4! text-base! leading-4!">{{ icon() }}</mat-icon>
    </span>

    <input
      class="w-full rounded-md border border-neutral-200 bg-neutral-0 py-1.5 pr-7 pl-7 text-sm text-neutral-800 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:bg-neutral-50 disabled:text-neutral-400"
      [class]="filled() ? 'placeholder:text-neutral-800' : 'placeholder:text-neutral-400'"
      type="text"
      autocomplete="off"
      [name]="name()"
      [attr.aria-label]="label()"
      [attr.aria-busy]="loading() || null"
      [disabled]="disabled()"
      [placeholder]="placeholder()"
      [ngModel]="value()"
      (ngModelChange)="valueChange.emit($event)"
      (focus)="opened.emit()"
      (blur)="close()" />

    @if (loading()) {
      <guarantees-spinner class="pointer-events-none absolute inset-y-0 right-2 text-neutral-400" />
    }

    <!-- For a control that answers from a closed list and offers to unfold it rather than to search. -->
    <ng-content />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFieldComponent {
  /** What is being looked up, said before the field is typed into. */
  icon = input<string>('search');
  name = input.required<string>();
  placeholder = input<string>('');
  /**
   * What the field is for, for a reader who never sees the placeholder. Falls back to the
   * placeholder, which names the field on most pickers; state it where the placeholder reports a
   * condition ("choose a partner first") instead of naming what is being searched.
   */
  ariaLabel = input<string>('');
  value = input<string>('');
  /** An answer is in flight. */
  loading = input(false, { transform: booleanAttribute });
  /**
   * The placeholder is standing in for a chosen value — a picker shows its selection there so
   * typing always starts a fresh search — and a value must read as one, not as a grey hint.
   */
  filled = input(false, { transform: booleanAttribute });
  /** Set where the field depends on an answer that has not been given yet. */
  disabled = input(false, { transform: booleanAttribute });

  valueChange = output<string>();
  opened = output<void>();
  /** Already deferred, so a click on a result lands before the list goes. */
  closed = output<void>();

  protected readonly label = computed(() => this.ariaLabel() || this.placeholder() || null);

  private pending?: ReturnType<typeof setTimeout>;

  constructor() {
    // The close is deferred, so the field can be gone before it fires — a blur is often what
    // removes the container holding it — and the timer would report to a caller that has left.
    inject(DestroyRef).onDestroy(() => clearTimeout(this.pending));
  }

  protected close(): void {
    clearTimeout(this.pending);
    this.pending = setTimeout(() => this.closed.emit(), CLOSE_DELAY_MS);
  }
}
